import React, { useState, useEffect } from 'react';
import { Button, Container, Typography, Box } from '@mui/material';
import { useCacxiStripe } from '../../../hooks/dashboard/useStripe';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';

import SubscriptionPlans from './SubscriptionPlans';
import AddonsCredits from './AddonsCredits';
// import PaymentForm from './PaymentForm';
import { LogOut } from 'lucide-react';
import CustomerBillingHistory, { BillingItem } from './CustomerBillingHistory';
import AddonHistoryTable, { AddonHistoryItem } from './AddonHistoryTable';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../../../store/slices/authSlice';

// Define TypeScript interfaces
interface PlanType {
  id: string;
  product?: {
    name?: string;
  };
  unit_amount?: number;
  recurring?: {
    interval?: string;
  };
}

const BillingView: React.FC = () => {
  // Plans and addons
  const [_selectedPlanDetails, setSelectedPlanDetails] = useState<PlanType | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});
  const [isLoading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const {
    recurrencePlans,
    oneOfPlans,
    getSubscriptionInfo,
    getCustomerCardDetails,
    replaceCustomerCard,
    getCustomerBillingHistory,
    getCustomerAddonHistory,
    cancelAtPeriodEnd,
    renewSubscription,
    createRenewalCheckoutSession,
    startImmediateSubscription,
    createPaymentMethod,
  } = useCacxiStripe();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  // Get current plan ID and subscription info from organization
  const currentPlanId = selectedOrganization?.planId || '';
  const isTrialEnabled = selectedOrganization?.isTrailEnabled || false;
  const stripeCustomerId = selectedOrganization?.stripeCustomerId || '';
  const [selectedPlan, setSelectedPlan] = useState<string>(currentPlanId);
  const [_currentPlanDetails, setCurrentPlanDetails] = useState<PlanType | null>(null);
  // State for subscription details
  const [_subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  // State for Customer Card Details
  const [customerCardDetails, setCustomerCardDetails] = useState<any>(null);

  // State for Customer Billing History
  const [customerBillingHistory, setCustomerBillingHistory] = useState<BillingItem[]>([]);
  const [customerAddonHistory, setCustomerAddonHistory] = useState<AddonHistoryItem[]>([]);

  // Trial information
  const trialEndDate = new Date(selectedOrganization?.subscriptionEndDate || '');
  const today = new Date();
  const diffTime = trialEndDate.getTime() - today.getTime();
  const trialDaysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const isTrialActive = isTrialEnabled && trialDaysRemaining > 0;
  const showLogout = isTrialActive && trialDaysRemaining <= 0;

  const subscriptionId = selectedOrganization?.stripeSubscriptionId || '';

  if (!subscriptionId) {
    console.error('No subscription ID found for the selected organization.');
  }

  // Set initial selected plan details and current plan details
  useEffect(() => {
    if (currentPlanId) {
      const currentPlan = [...recurrencePlans].find(plan => plan.id === currentPlanId);
      if (currentPlan) {
        setCurrentPlanDetails(currentPlan);
        // Only set as selected plan if in trial mode or changing plans
        setSelectedPlan(currentPlanId);
        setSelectedPlanDetails(currentPlan);
      }
    } else if (recurrencePlans.length > 0 && isTrialActive) {
      setSelectedPlan(recurrencePlans[0].id);
      setSelectedPlanDetails(recurrencePlans[0]);
    }
  }, [currentPlanId, recurrencePlans, isTrialActive]);

  const handleUpdateAddon = (id: string, quantity: number): void => {
    setSelectedAddons(prev => ({
      ...prev,
      [id]: quantity,
    }));
  };

  const handleCardDetailsUpdate = (details: any) => {
    setCustomerCardDetails(details);
  };

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      // Card details are independent of the selected plan. Organizations that
      // have not selected a plan yet should still be able to view/manage their
      // payment method.
      if (!stripeCustomerId) {
        console.log('Missing required IDs, skipping fetch');
        return;
      }

      console.log('Fetching subscription details...');
      setLoading(true);

      try {
        console.log('Current values:', {
          stripeCustomerId,
          isTrialEnabled,
          currentPlanId,
        });

        const [details, cardDetails, billingHistory, addonHistory] = await Promise.all([
          currentPlanId ? getSubscriptionInfo(stripeCustomerId) : Promise.resolve(null),
          getCustomerCardDetails(stripeCustomerId),
          getCustomerBillingHistory(stripeCustomerId),
          getCustomerAddonHistory(stripeCustomerId),
        ]);

        console.log('Fetched data:', {
          details,
          cardDetails,
          billingHistory,
          addonHistory,
        });

        const rawCardData = cardDetails?.[0];
        const processedCardDetails = rawCardData
          ? {
              id: rawCardData.id,
              brand: rawCardData.card?.brand,
              last4: rawCardData.card?.last4,
              exp_month: rawCardData.card?.exp_month,
              exp_year: rawCardData.card?.exp_year,
              billing_email: rawCardData.billing_details?.email,
              name: rawCardData.billing_details?.name,
            }
          : undefined;

        setSubscriptionDetails(details);
        setCustomerCardDetails(processedCardDetails);
        setCustomerBillingHistory(billingHistory || []);
        setCustomerAddonHistory(addonHistory || []);
      } catch (error) {
        console.error('Error fetching subscription details:', error);
        // Consider adding error state to show to user
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [stripeCustomerId, isTrialEnabled, currentPlanId]);

  useEffect(() => {}, [
    stripeCustomerId,
    isTrialEnabled,
    currentPlanId,
    getSubscriptionInfo,
    getCustomerCardDetails,
    getCustomerBillingHistory,
    getCustomerAddonHistory,
  ]);

  console.log('Selected Addons:', selectedOrganization?.stripeSubscriptionId);

  if (!recurrencePlans || recurrencePlans.length === 0) {
    return (
      <Box className="h-screen flex items-center justify-center font-sans login-bg">
        <Typography className="text-white text-lg font-bold">Loading plans...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {
        <>
          {showLogout && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogOut size={16} />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          )}
          <SubscriptionPlans
            recurrencePlans={recurrencePlans}
            selectedPlan={selectedPlan}
            isTrialActive={isTrialActive}
            customerCardDetails={customerCardDetails}
            stripeCustomerId={stripeCustomerId}
            replaceCustomerCard={replaceCustomerCard}
            createPaymentMethod={createPaymentMethod}
            getCustomerCardDetails={getCustomerCardDetails}
            onCardDetailsUpdate={handleCardDetailsUpdate}
            trialDaysRemaining={trialDaysRemaining}
            startImmediateSubscription={startImmediateSubscription}
            cancelAtPeriodEnd={cancelAtPeriodEnd}
            renewSubscription={renewSubscription}
            renewalCheckout={createRenewalCheckoutSession}
            subscriptionId={subscriptionId}
            isLoading={isLoading}
          />

          <CustomerBillingHistory history={customerBillingHistory} isLoading={isLoading} />
          <AddonHistoryTable addonHistory={customerAddonHistory} isLoading={isLoading} />

          {false && (
            <AddonsCredits
              oneOfPlans={oneOfPlans}
              selectedAddons={selectedAddons}
              onUpdateAddon={handleUpdateAddon}
            />
          )}
        </>
      }
    </Container>
  );
};

export default BillingView;
